import {
  Box,
  Button,
  Divider,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Select,
  Text as ChakraText,
  useDisclosure,
} from '@chakra-ui/react';
import { usePaginationAdmin } from '@diamond/shared/context';
import type { SelectOption } from '@diamond/shared/types';
import {
  Breadcrumbs,
  Card,
  GridTable,
  Pagination,
  Text,
} from '@diamond/shared/ui';
import { AdminLayout } from '@diamond/sol-admin/components';
import { useCleanSearchParams } from '@diamond/sol-admin/hooks';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ControlPointDuplicateIcon from '@mui/icons-material/ControlPointDuplicate';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditIcon from '@mui/icons-material/Edit';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { CollectionFilterBar } from '../components/collection-filter-bar';
import { CreateLHCModal } from '../components/create-lhc-modal';
import { EditLHCModal } from '../components/edit-lhc-modal';
import { SyncInfo } from '../components/sync-info';
import { PLANT_OPTIONS } from '../constants';
import {
  useCollectionPlants,
  useLHCExportMutation,
  useLHCList,
} from '../hooks';
import type {
  CollectionDateFilterFields,
  LHCRowData,
} from '../types';
import { buildLhcFilter } from '../utils/build-lhc-params';

const links = [
  { title: 'Beranda', link: '/' },
  { title: 'Collection Management', link: '/collection-management' },
];

const DEFAULT_PAGE_SIZE = 50;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const getCurrentPageFromUrl = (params: URLSearchParams): number => {
  return parseInt(params.get('page') || '1', 10);
};

const getPageSizeFromUrl = (params: URLSearchParams): number => {
  const pageSize = parseInt(
    params.get('pageSize') || DEFAULT_PAGE_SIZE.toString(),
    10
  );
  return PAGE_SIZE_OPTIONS.includes(pageSize) ? pageSize : DEFAULT_PAGE_SIZE;
};

type ActionCellRendererParams = {
  onExportLHC: (id: string) => Promise<unknown>;
  onEditLHC: (row: LHCRowData) => void;
};

function ActionCellRenderer(
  params: ActionCellRendererParams & ICellRendererParams<LHCRowData>
) {
  const handleExport = async () => {
    const data = params.data;
    if (params.onExportLHC && data?.id) {
      try {
        await params.onExportLHC(data.id);
      } catch (error) {
        // Error is handled in the mutation
      }
    }
  };

  const handleEdit = () => {
    const data = params.data;
    if (data && params.onEditLHC) {
      params.onEditLHC(data);
    }
  };

  return (
    <Flex
      align="center"
      h="100%"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <Menu>
        <MenuButton
          as={Button}
          size="sm"
          variant="outline"
          borderColor="#0000a3"
          color="#0000a3"
          borderRadius="md"
          rightIcon={<KeyboardArrowDownIcon fontSize="small" />}
          _hover={{ bgColor: 'blue.50' }}
          _active={{ bgColor: 'blue.100' }}
        >
          Action
        </MenuButton>
        <Portal>
          <MenuList
            zIndex={2000}
            fontFamily="'Libre Franklin', -apple-system, system-ui, sans-serif"
            fontSize="14px"
            fontWeight={400}
            lineHeight="20px"
            letterSpacing="0%"
          >
            <MenuItem onClick={handleExport}>
              <Flex align="center" gap={2}>
                <Box
                  as="span"
                  display="inline-flex"
                  w={4}
                  h={4}
                  flexShrink={0}
                  alignItems="center"
                  justifyContent="center"
                >
                  <FileDownloadIcon style={{ fontSize: 16 }} />
                </Box>
                Export .xls
              </Flex>
            </MenuItem>
            <MenuItem onClick={handleEdit}>
              <Flex align="center" gap={2}>
                <Box
                  as="span"
                  display="inline-flex"
                  w={4}
                  h={4}
                  flexShrink={0}
                  alignItems="center"
                  justifyContent="center"
                >
                  <EditIcon style={{ fontSize: 16 }} />
                </Box>
                Edit
              </Flex>
            </MenuItem>
            <MenuItem>
              <Flex align="center" gap={2}>
                <Box
                  as="span"
                  display="inline-flex"
                  w={4}
                  h={4}
                  flexShrink={0}
                  alignItems="center"
                  justifyContent="center"
                >
                  <ControlPointDuplicateIcon style={{ fontSize: 16 }} />
                </Box>
                Duplicate
              </Flex>
            </MenuItem>
            <MenuItem color="red">
              <Flex align="center" gap={2}>
                <Box
                  as="span"
                  display="inline-flex"
                  w={4}
                  h={4}
                  flexShrink={0}
                  alignItems="center"
                  justifyContent="center"
                >
                  <DeleteForeverIcon style={{ fontSize: 16 }} />
                </Box>
                Delete
              </Flex>
            </MenuItem>
          </MenuList>
        </Portal>
      </Menu>
    </Flex>
  );
}

export function CollectionManagement() {
  const navigate = useNavigate();

  // URL-based pagination
  const { searchParams: urlParams, setParams } = useCleanSearchParams();
  const currentPage = getCurrentPageFromUrl(urlParams);
  const pageSize = getPageSizeFromUrl(urlParams);

  const setCurrentPage = useCallback(
    (page: number) => {
      setParams({ page: page.toString() });
    },
    [setParams]
  );

  const setPageSize = useCallback(
    (size: number) => {
      setParams({ pageSize: size.toString(), page: '1' });
    },
    [setParams]
  );

  // Create LHC modal state
  const {
    isOpen: isCreateLHCOpen,
    onOpen: onCreateLHCOpen,
    onClose: onCreateLHCClose,
  } = useDisclosure();
  const {
    isOpen: isEditLHCOpen,
    onOpen: onEditLHCOpen,
    onClose: onEditLHCClose,
  } = useDisclosure();
  const [selectedLHCForEdit, setSelectedLHCForEdit] =
    useState<LHCRowData | null>(null);

  // Filter state
  const [plantValue, setPlantValue] = useState<SelectOption | null>(null);
  const [cabangValue, setCabangValue] = useState<SelectOption | null>(null);

  // Fetch plant options from API, fallback to hardcoded
  const { data: collectionPlantsData, refetch: refetchCollectionPlants } =
    useCollectionPlants();
  const plantOptions = useMemo(() => {
    if (collectionPlantsData?.data?.length) {
      return collectionPlantsData.data.map((p) => ({
        label: p.name,
        value: p.id,
      }));
    }
    return PLANT_OPTIONS;
  }, [collectionPlantsData]);

  // Date form management
  const methods = useForm<CollectionDateFilterFields>({
    defaultValues: {
      'lhc_date-from': '',
      'lhc_date-to': '',
    },
  });

  const dateFrom = methods.watch('lhc_date-from');
  const dateTo = methods.watch('lhc_date-to');

  // Search form with default value
  const { register: searchRegister, watch } = useForm({
    defaultValues: { search: '' },
  });

  const searchQuery = watch('search');

  const apiParams = useMemo(
    () =>
      buildLhcFilter({
        page: currentPage,
        size: pageSize,
        dateFrom,
        dateTo,
        plantValue,
        cabangValue,
        searchQuery,
      }),
    [
      currentPage,
      pageSize,
      dateFrom,
      dateTo,
      plantValue,
      cabangValue,
      searchQuery,
    ]
  );

  // Fetch LHC list from API
  const { data: apiData, isLoading, refetch } = useLHCList(apiParams);

  // Export mutation
  const { exportLHC } = useLHCExportMutation();

  // Reset page to 1 when filters change
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, plantValue, cabangValue, dateFrom, dateTo]);

  const handleSearch = useCallback(() => {
    // Search is driven by searchQuery via apiParams, trigger page reset
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [currentPage, setCurrentPage]);

  const handleDateFilter = useCallback((period: string) => {
    return { fromDate: '', toDate: '' };
  }, []);

  const updateDateParams = useCallback(
    (fromDate: string, toDate: string) => {
      methods.setValue('lhc_date-from', fromDate);
      methods.setValue('lhc_date-to', toDate);
    },
    [methods]
  );

  const handlePlantChange = useCallback((newValue: SelectOption | null) => {
    setPlantValue(newValue);
  }, []);

  const handleCabangChange = useCallback((newValue: SelectOption | null) => {
    setCabangValue(newValue);
  }, []);

  const handleEditLHCOpen = useCallback(
    (row: LHCRowData) => {
      setSelectedLHCForEdit(row);
      onEditLHCOpen();
    },
    [onEditLHCOpen]
  );

  const handleEditLHCClose = useCallback(() => {
    setSelectedLHCForEdit(null);
    onEditLHCClose();
  }, [onEditLHCClose]);

  // Pagination: server-side from API
  const totalPages = useMemo(() => {
    return apiData?.pagination?.pages ?? 0;
  }, [apiData]);

  const validCurrentPage = useMemo(() => {
    if (currentPage < 1) return 1;
    if (currentPage > totalPages && totalPages > 0) return totalPages;
    return currentPage;
  }, [currentPage, totalPages]);

  useEffect(() => {
    if (validCurrentPage !== currentPage) {
      setCurrentPage(validCurrentPage);
    }
  }, [validCurrentPage, currentPage, setCurrentPage]);

  // Data for the table from API
  const tableData = useMemo(() => {
    return apiData?.data ?? [];
  }, [apiData]);

  // Pagination controls
  const { nextPage, previousPage, lastPage, firstPage } = usePaginationAdmin(
    validCurrentPage,
    setCurrentPage,
    totalPages
  );

  const columnDefs = useMemo<ColDef<LHCRowData>[]>(
    () => [
      {
        field: 'no',
        headerName: 'No.',
        width: 60,
        pinned: 'left',
        resizable: false,
      },
      { field: 'lhcDate', headerName: 'Tgl. LHC', width: 110, pinned: 'left' },
      {
        field: 'lhcNo',
        headerName: 'No. LHC',
        width: 160,
        pinned: 'left',
        cellStyle: { color: '#0000A3', cursor: 'pointer', fontWeight: 500 },
      },
      { field: 'plant', headerName: 'Plant', minWidth: 80, flex: 1 },
      {
        field: 'totalInvoice',
        headerName: 'Total Faktur',
        minWidth: 100,
        flex: 1,
      },
      {
        field: 'totalInvoiceValue',
        headerName: 'Total Nilai Faktur',
        minWidth: 150,
        flex: 1,
      },
      {
        field: 'mutationValue',
        headerName: 'Nilai Mutasi',
        minWidth: 150,
        flex: 1,
      },
      {
        field: 'totalDifference',
        headerName: 'Total Selisih +/-',
        minWidth: 140,
        flex: 1,
      },
      {
        field: 'lastUpdated',
        headerName: 'Terakhir diperbarui',
        minWidth: 160,
        flex: 1,
      },
      {
        colId: 'actions',
        headerName: '',
        width: 130,
        pinned: 'right',
        cellRenderer: ActionCellRenderer,
        cellRendererParams: {
          onExportLHC: exportLHC,
          onEditLHC: handleEditLHCOpen,
        } as ActionCellRendererParams,
        sortable: false,
        filter: false,
        resizable: false,
      },
    ],
    [exportLHC, handleEditLHCOpen]
  );

  return (
    <AdminLayout>
      <Box paddingBottom={4}>
        <Breadcrumbs links={links} useRouterDom />
      </Box>
      <Box py={4}>
        <Flex justify="space-between" align="center" mb={6}>
          <Flex align="center" gap={3}>
            <IconButton
              aria-label="Kembali"
              icon={<ChevronLeft sx={{ fontSize: 24 }} />}
              variant="ghost"
              size="sm"
              onClick={() => navigate('/')}
            />
            <Text as="h5" fontSize="32px" fontWeight="bold">
              Laporan Harian Collection
            </Text>
          </Flex>
          <Box maxW="453px">
            <SyncInfo lastSyncedAt={undefined} onSync={() => refetch()} />
          </Box>
        </Flex>
        <Divider mb={4} />

        {/* Filter Bar with Buat LHC button and filters */}
        <Flex
          mb={4}
          gap={3}
          alignItems={{ base: 'stretch', md: 'center' }}
          justifyContent="space-between"
          flexWrap="wrap"
        >
          {/* Left side: Buat LHC button + filters */}
          <Flex
            gap={3}
            width="full"
            alignItems={{ base: 'stretch', md: 'center' }}
            flexWrap="wrap"
          >
            {/* Filters */}
            <CollectionFilterBar
              methods={methods}
              searchRegister={searchRegister}
              handleSearch={handleSearch}
              handleDateFilter={handleDateFilter}
              updateDateParams={updateDateParams}
              plantValue={plantValue}
              onPlantChange={handlePlantChange}
              plantOptions={plantOptions}
              cabangValue={cabangValue}
              onCabangChange={handleCabangChange}
              onCreateLHC={onCreateLHCOpen}
            />
          </Flex>
        </Flex>

        <Card p="0">
          <GridTable<LHCRowData>
            columnDefs={columnDefs}
            rowData={tableData}
            isLoading={isLoading}
            pagination={false}
            onRowClick={(data) => {
              if (data?.id) {
                navigate(`/collection-management/${data.id}`);
              }
            }}
            suppressRowClickForColumns={['actions']}
            gridOptions={{
              defaultColDef: {
                resizable: true,
                sortable: true,
              },
              domLayout: 'normal',
            }}
          />
        </Card>

        {/* External Pagination */}
        <Flex justify="center" align="center" gap={4} mt={4}>
          <Pagination
            currentPage={validCurrentPage}
            totalPage={totalPages}
            nextPage={nextPage}
            previousPage={previousPage}
            firstPage={firstPage}
            lastPage={lastPage}
          />
          <Flex align="center" gap={2} fontSize="14px">
            <ChakraText>per halaman</ChakraText>
            <Select
              size="sm"
              width="80px"
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              borderColor="gray.33"
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </Select>
          </Flex>
        </Flex>
      </Box>

      <CreateLHCModal
        isOpen={isCreateLHCOpen}
        onClose={onCreateLHCClose}
        plantOptions={plantOptions}
        lastSyncedAt={undefined}
        onSync={() => refetchCollectionPlants()}
      />

      <EditLHCModal
        isOpen={isEditLHCOpen}
        onClose={handleEditLHCClose}
        initialData={
          selectedLHCForEdit
            ? {
                id: selectedLHCForEdit.id,
                lhcNo: selectedLHCForEdit.lhcNo,
                lhcDateRaw: selectedLHCForEdit.lhcDateRaw,
                collectionPlantId: selectedLHCForEdit.collectionPlantId,
                updatedAtUnix: selectedLHCForEdit.updatedAtUnix,
              }
            : null
        }
        plantOptions={plantOptions}
        lastSyncedAt={selectedLHCForEdit?.updatedAtUnix}
        onSync={() => refetchCollectionPlants()}
      />
    </AdminLayout>
  );
}

export default CollectionManagement;
